import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import { EditorConfigContext } from '../contexts/EditorConfigContext';
import useDebouncedEditorStateContext from '../contexts/useDebouncedEditorStateContext';
import { PERSONALIZATION_TOKEN } from '../editorCapabilities/constants';
import useContentSearch from '../utils/useContentSearch';
import { buildPersonalizationTokenTr } from '../utils/documentUtils';

/**
 * Custom styles to apply to personalization token blocks in the editor.
 * These override the default token appearance. See fallbacks in PersonalizationTokenView.
 */

/**
 * Represents a single personalization token that can be inserted into the editor.
 * Tokens are placeholders that get replaced with actual values when the content is sent.
 */

import { View } from 'foundations-components/components/View';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { PopoverBody } from 'foundations-components/transitional/components/Popover';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { Button } from 'foundations-components/transitional/components/Button';
import { Select } from 'foundations-components/transitional/components/Select';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const noop = () => {};

/** Configuration for the Personalization Token capability (merge fields) */

const DefaultTokenValueInput = ({
  value,
  onChange
}) => {
  return /*#__PURE__*/_jsx(FormControl, {
    label: I18n.text('hubspot-prosemirror.personalizationToken.popover.defaultValue'),
    marginBlockStart: "100",
    children: /*#__PURE__*/_jsx(TextInput, {
      autoFocus: true,
      onChange: onChange,
      value: value,
      placeholder: I18n.text('hubspot-prosemirror.personalizationToken.popover.defaultValuePlaceholder')
    })
  });
};
const tokenPrefixToMessageKeyMap = {
  company: 'hubspot-prosemirror.personalizationToken.tokenGroups.company',
  contact: 'hubspot-prosemirror.personalizationToken.tokenGroups.contact',
  deal: 'hubspot-prosemirror.personalizationToken.tokenGroups.deal',
  host: 'hubspot-prosemirror.personalizationToken.tokenGroups.host',
  meeting: 'hubspot-prosemirror.personalizationToken.tokenGroups.meeting',
  quote: 'hubspot-prosemirror.personalizationToken.tokenGroups.quote',
  sender: 'hubspot-prosemirror.personalizationToken.tokenGroups.sender',
  ticket: 'hubspot-prosemirror.personalizationToken.tokenGroups.ticket'
};
function getLabelForTokenPrefix(tokenPrefix) {
  return I18n.text(tokenPrefixToMessageKeyMap[tokenPrefix]);
}
function isOptionsWithTokenStylesGrouped(options) {
  if (!options.length) throw new Error('Options array is empty');
  return options[0].options !== undefined;
}
export function transformOptions(data, groupTokens) {
  if (groupTokens) {
    if (!data) return null;
    const groupedOptions = data.reduce((acc, token) => {
      const tokenPrefix = token.groupName || token.tokenKey.split('.')[0];
      if (!acc[tokenPrefix]) {
        acc[tokenPrefix] = {
          options: [],
          text: token.groupName || getLabelForTokenPrefix(tokenPrefix),
          value: tokenPrefix
        };
      }
      acc[tokenPrefix].options.push({
        text: token.displayText,
        tokenStyles: token.tokenStyles,
        value: token.tokenKey
      });
      return acc;
    }, {});
    return Object.values(groupedOptions);
  }
  return data && data.map(token => ({
    text: token.displayText,
    value: token.tokenKey,
    tokenStyles: token.tokenStyles
  }));
}
export function PersonalizationTokenPopover({
  initialTokenKey,
  initialDefaultValue = '',
  nodePos,
  onPopoverConfirm
}) {
  const shouldInsertNewToken = nodePos === undefined;
  const {
    [PERSONALIZATION_TOKEN]: personalizationTokenConfig
  } = useContext(EditorConfigContext);
  const {
    fetchTokens,
    groupTokens,
    allowDefaultValue,
    defaultValue
  } = personalizationTokenConfig;
  const showDefaultValueInput = Boolean(defaultValue) || allowDefaultValue;
  const isDefaultValueRequired = defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue.required;
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const [defaultTokenValue, setDefaultTokenValue] = useState(initialDefaultValue);
  const [selectedToken, setSelectedToken] = useState(initialTokenKey || null);
  const transformFunction = useMemo(() => data => transformOptions(data, groupTokens), [groupTokens]);
  const [__data, searchQuery, setSearchQuery, options, isLoading] = useContentSearch(fetchTokens, transformFunction, noop);
  const _inputRef = useRef(null);
  const prevOption = usePrevious(options);
  const wasLoading = usePrevious(isLoading);
  useEffect(() => {
    const didPreviouslyRenderTypeahead = prevOption && prevOption.length > 0;
    const isTypeaheadRendered = options && options.length > 0;
    if ((!didPreviouslyRenderTypeahead && isTypeaheadRendered || wasLoading && !isLoading) && _inputRef && _inputRef.current) {
      _inputRef.current.focus();
    }
  }, [options, _inputRef, isLoading, wasLoading, prevOption]);
  const handleSearch = useCallback(value => setSearchQuery(value), [setSearchQuery]);
  const handleChange = useCallback(e => {
    setSelectedToken(e.target.value);
  }, [setSelectedToken]);
  const handleOnInsert = useCallback(() => {
    if (!selectedToken) {
      return;
    }
    const selectedOption = function () {
      if (!options) return undefined;
      let flatOptions = options;
      if (isOptionsWithTokenStylesGrouped(options)) {
        flatOptions = options.flatMap(group => group.options);
      }
      return flatOptions.find(option => option.value === selectedToken);
    }();
    const tr = buildPersonalizationTokenTr({
      tokenKey: selectedToken,
      displayText: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.text,
      tokenStyles: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.tokenStyles,
      defaultTokenValue,
      editorState,
      nodePos
    });
    setEditorState(editorState.apply(tr));
    onPopoverConfirm();
  }, [editorState, setEditorState, defaultTokenValue, selectedToken, options, nodePos, onPopoverConfirm]);

  // This prevents the typeahead from closing when the user presses the spacebar.
  // Typically, this keydown bubbles up to the Dropdown component, and causes
  // the dropdown to close. For any keyboard events that are targeted at the input
  // we want to prevent that from happening.
  // The escape key can still be used to close the dropdown.
  const keydownInterceptor = useCallback(e => {
    if (e.target instanceof HTMLInputElement && e.key !== 'Escape') {
      e.stopPropagation();
    }
  }, []);
  const disableInsertToken = selectedToken === null || selectedToken === undefined || isDefaultValueRequired && !defaultTokenValue;
  if (isLoading && searchQuery.length === 0) {
    return /*#__PURE__*/_jsx(Flex, {
      minBlockSize: "100px",
      justifySelf: "center",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {})
    });
  }
  if (!options) {
    return /*#__PURE__*/_jsx(View, {
      margin: "200",
      as: "div",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.personalizationToken.popover.error"
      })
    });
  }
  return /*#__PURE__*/_jsxs(PopoverBody, {
    children: [/*#__PURE__*/_jsxs("div", {
      onKeyDown: keydownInterceptor,
      children: [/*#__PURE__*/_jsx(FormControl, {
        label: shouldInsertNewToken ? I18n.text('hubspot-prosemirror.personalizationToken.popover.addContactToken') : I18n.text('hubspot-prosemirror.personalizationToken.popover.updateContactToken'),
        children: /*#__PURE__*/_jsx(Select, {
          "data-test-id": "personalization-token-select",
          options: options,
          placeholder: I18n.text('hubspot-prosemirror.personalizationToken.popover.searchPlaceholder'),
          onInputChange: handleSearch,
          onChange: handleChange,
          inputRef: _inputRef,
          value: selectedToken
        })
      }), showDefaultValueInput && /*#__PURE__*/_jsx(DefaultTokenValueInput, {
        value: defaultTokenValue,
        onChange: e => setDefaultTokenValue(e.target.value)
      })]
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "500",
      children: /*#__PURE__*/_jsx(Button, {
        onClick: handleOnInsert,
        use: "tertiary",
        size: "sm",
        disabled: disableInsertToken,
        children: I18n.text(shouldInsertNewToken ? 'hubspot-prosemirror.personalizationToken.popover.insertButton' : 'hubspot-prosemirror.personalizationToken.popover.updateButton')
      })
    })]
  });
}
PersonalizationTokenPopover.displayName = 'PersonalizationTokenPopover';
function InsertPersonalizationToken({
  buttonText
}) {
  const [open, setOpen] = useState(false);
  const onPopoverConfirm = useCallback(() => {
    setOpen(false);
  }, []);
  const onOpenChange = e => setOpen(e.target.value);
  const ContentWrapper = useCallback(() => /*#__PURE__*/_jsx(PersonalizationTokenPopover, {
    onPopoverConfirm: onPopoverConfirm
  }), [onPopoverConfirm]);
  return /*#__PURE__*/_jsx(Dropdown, {
    "data-test-id": "personalization-token",
    buttonUse: "transparent",
    menuWidth: 400,
    padding: "0",
    open: open,
    onOpenChange: onOpenChange,
    buttonText: buttonText || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "hubspot-prosemirror.personalizationToken.toolbarButton"
    }),
    Content: ContentWrapper
  });
}
InsertPersonalizationToken.displayName = 'InsertPersonalizationToken';
export default InsertPersonalizationToken;